import React, {useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './InspectedCellPanel.css';
import {showConstructionPhaseInfoModal} from '../../../redux/actions';
import {useDesktopLayout} from '../../../hooks/visuals/useWideScreenLayout';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {RULES_SECTION_IDS} from '../../Modals/RulesModal/rulesSections';
import {
  selectBuildingsDamage,
  selectCurrentPlayer,
  selectFortressesUnderConstruction,
  selectGameType,
  selectGroundBackground,
  selectHexes,
  selectPendingRecruitmentActions,
  selectPendingSupply,
  selectPhase,
  selectPlayers,
  selectRound,
  selectSpecialBuildings,
  selectUnits
} from '../../../redux/reducers/sharedStateReducer/selectors';
import {selectInspectedHexKey, selectUserId} from '../../../redux/reducers/privateStateReducer/selectors';
import {getUnitSizeWithPendingRecruitment} from '../../../utils/gameState/recruitment/getUnitSizeWithPendingRecruitment';
import {getActingPlayer} from '../../../utils/players/getActingPlayer';
import {PHASES} from '../../../constants/phases';
import {checkIfUnitHasPendingAdditionalSupply} from '../../../utils/gameState/checkIfUnitHasPendingAdditionalSupply';
import {
  COMMON_BUILDING_CONFIGS,
  KEY_POINT_CONFIGS,
  SPECIAL_BUILDING_CONFIGS,
  TERRAIN_CONFIGS
} from '../../../configs/tokens';
import {
  CENTRAL_QUARTER_PREFIX,
  COMMON_BUILDING_TOKENS,
  KEY_POINT_TOKENS,
  SPECIAL_BUILDING_TOKENS,
  TERRAIN_TOKENS
} from '../../../constants/tokens';
import {checkIfCentralQuarter} from '../../../utils/buildings/checkIfCentralQuarter';
import {getMountainImage} from '../../../configs/mountainTiles';
import {findOwner} from '../../../utils/gameState/findOwner';
import {checkIfOwner} from '../../../utils/gameState/checkIfOwner';
import {checkIfHexBlockedByEnemyUnit} from '../../../utils/gameState/checkIfHexBlockedByEnemyUnit';
import {checkIfFortressConstructionBlocked} from '../../../utils/gameState/construction/checkIfFortressConstructionBlocked';
import {checkIfAdjacentToWorkingWarehouse} from '../../../utils/gameState/supply/checkIfAdjacentToWorkingWarehouse';
import {
  selectFieldWithConstructionPlan
} from '../../../utils/gameState/construction/planning/selectFieldWithConstructionPlan';
import {
  calculateBaseQuarterAllowedCells,
  calculateQuarterZoneCells
} from '../../../utils/gameState/construction/calculateQuarterZoneCells';
import {FARM_SUPPLY_STANDARD_COUNT, FARM_SUPPLY_WITH_WAREHOUSE_COUNT} from '../../../constants/supply';
import {NEUTRAL_PLAYER_COLOR} from '../../../constants/players';
import {getPlayerSurfaceColor} from '../../../utils/players/getPlayerHexColor';
import FactionSkinIcon from '../../RankSkinFrame/FactionSkinIcon';
import {getEnemyCitiesForPlayer} from '../../../utils/gameState/getEnemyCitiesForPlayer';
import {calcMineIncome} from '../../../utils/gameState/income/calcMineIncome';
import {calcRuinsIncome} from '../../../utils/gameState/income/calcRuinsIncome';
import {selectRuinsIncomeRoundLimit} from '../../../utils/gameState/income/checkIfRoundWithIncreasedRuinsIncome';
import {calcGuildIncome} from '../../../utils/gameState/income/calcGuildIncome';
import {calcTempleIncome} from '../../../utils/gameState/income/calcTempleIncome';
import {CoinsStat} from '../../PlayerStat/stats/CoinsStat';
import {WinPointsStat} from '../../PlayerStat/stats/WinPointsStat';
import BuildingDescription from '../../BuildingDescription/BuildingDescription';
import CentralQuarterIcon from '../CentralQuarterIcon/CentralQuarterIcon';
import RecruitUnitCard from '../NewUnitToolbar/RecruitUnitCard/RecruitUnitCard';
import {getPlayerColorStyle} from '../../../utils/gameUi/getPlayerColorStyle';
import TokenHero from '../TokenHero/TokenHero';
import {getUnitTypesForRecruitment} from '../../../utils/unit/getUnitTypesForRecruitment';
import {getPlayerById} from '../../../utils/players/getPlayerById';
import {getNeutralPlayerMock} from '../../../utils/players/getNeutralPlayerMock';
import {calculateCitiesCache} from '../../../utils/gameState/calculateCitiesCache';
import {
  checkIfBarracksNearWorkingMilitaryCamp,
  getSupplyPreviewPlayers
} from '../../../utils/hexes/calculateTokenImage';
import {UNIT_SIZE_FOR_SUPPLY_WITH_MILITARY_CAMP} from '../../../constants/units';
import {MASKED_ICON_TONES, MaskedIcon} from '../../coloredIcons/MaskedIcon/MaskedIcon';
import {checkIfSupplyRecruitHiddenFromViewer} from '../../../utils/unit/checkIfSupplyRecruitHiddenFromViewer';

const MOUNTAIN_DESCRIPTION = 'Непроходимая ячейка.\nЗдесь нельзя строить.\nЧерез гору или по касательной нельзя стрелять.';
const SEA_DESCRIPTION = 'Непроходимая ячейка.\nЗдесь нельзя строить.';

// Иконка казармы, смежной работающему военному лагерю, — та же картинка, что на
// поле (текстура barracks_2, см. calculateTokenImage)
const BARRACKS_WITH_MILITARY_CAMP_ICON = '/img/hexes/barracks_2.319e3f5e.png';

const KEY_POINT_TOKEN_IDS = Object.values(KEY_POINT_TOKENS);

// Информация о выбранной ячейке во время чужого хода (см. handleInspectionClick
// в GameScene). Крупный жетон в рамке цвета владельца, рядом название, владелец, доход
// и описание; отряд на ячейке — та же карточка, что и на этапе найма (RecruitUnitCard):
// на десктопе крылом справа, вне десктопа под ячейкой
const InspectedCellPanel = () => {
  const dispatch = useDispatch();
  // На десктопе плита футера при осмотре шире (Footer.css), и описание строения
  // помещается рядом со статами отряда — без ссылки «Подробнее» (ниже)
  const isDesktopLayout = useDesktopLayout();
  // Полный стор — calculateQuarterZoneCells/calculateBaseQuarterAllowedCells
  // (см. пустую ячейку ниже) сами читают из него нужные им кусочки (hexes,
  // pendingBuildings, специальные строения, игроков), как и на самом поле
  // (см. HexGridRenderer.updateQuarterZoneOverlay) — а не принимают их по
  // отдельности
  const state = useSelector((s) => s);
  const hexKey = useSelector(selectInspectedHexKey);
  const hexes = useSelector(selectHexes);
  const groundBackground = useSelector(selectGroundBackground);
  const specialBuildings = useSelector(selectSpecialBuildings);
  const units = useSelector(selectUnits);
  const players = useSelector(selectPlayers);
  // Store-кэш городов пересчитывается только на COMPLETE_PHASE (конец хода
  // строительства) — в течение одного хода он не видит города/кварталы,
  // расширенные более ранними действиями этого же хода (см.
  // playNearbyGuildsTemplesIncomeChangeAnimation). Инспекция должна
  // показывать доход по факту, поэтому считаем кэш городов заново из
  // актуальных hexes/specialBuildings вместо чтения устаревшего store-кэша
  const citiesCache = useMemo(() => calculateCitiesCache(hexes, specialBuildings), [hexes, specialBuildings]);
  const buildingsDamage = useSelector(selectBuildingsDamage);
  const fortressesUnderConstruction = useSelector(selectFortressesUnderConstruction);
  const round = useSelector(selectRound);
  const ruinsIncomeRoundLimit = useSelector(selectRuinsIncomeRoundLimit);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const gameType = useSelector(selectGameType);
  const userId = useSelector(selectUserId);
  const phase = useSelector(selectPhase);
  const pendingSupply = useSelector(selectPendingSupply);
  const pendingRecruitmentActions = useSelector(selectPendingRecruitmentActions);

  if (!hexKey) {
    return null;
  }

  // Игрок за этим клиентом (в локальной игре — тот, чей сейчас ход) — его
  // цветом рисуется флаг центрального квартала в подсказке захвата
  const actingPlayer = getActingPlayer(gameType, userId, players, currentPlayer);

  // Поле таким, каким оно нарисовано: при планировании строительства в чужой
  // ход запланированные строения стоят на поле поверх настоящих (см.
  // selectFieldWithConstructionPlan) — и панель описывает их же: жетон,
  // владельца (запланированный квартал забирает ячейки и ключевые точки
  // рядом), доход и блокировку. Без плана на поле это само настоящее поле
  const fieldWithPlan = selectFieldWithConstructionPlan(state);
  const fieldHexes = fieldWithPlan.hexes;
  const fieldSpecialBuildings = fieldWithPlan.specialBuildings;
  const fieldCitiesCache = fieldWithPlan.citiesCache || citiesCache;

  const token = fieldHexes[hexKey];
  const specialBuilding = fieldSpecialBuildings[hexKey];
  // На ячейке запланированное строение, которого на настоящем поле нет
  const isPlannedBuilding = token !== hexes[hexKey] || specialBuilding !== specialBuildings[hexKey];
  // Чужой новобранец крысолюдов за снабжение не нарисован на поле — нет его и
  // в осмотре (см. checkIfSupplyRecruitHiddenFromViewer)
  const unit = units.find((u) => u.hexKey === hexKey && !checkIfSupplyRecruitHiddenFromViewer(u, state));
  const ownerPlayer = findOwner(hexKey, players, fieldCitiesCache);

  // Ячейки нейтральных городов: нейтрального игрока нет в players, но его
  // город (цвет neutral) есть в citiesCache — владельцем показываем «Нейтрала»
  const isNeutralCityHex = !ownerPlayer && checkIfOwner(hexKey, NEUTRAL_PLAYER_COLOR, fieldCitiesCache);
  const displayOwner = ownerPlayer || (isNeutralCityHex ? {
    factionId: NEUTRAL_PLAYER_COLOR,
    name: 'Нейтрал',
    color: NEUTRAL_PLAYER_COLOR,
  } : null);

  // Есть ли на ячейке строение с собственным описанием в правилах фазы
  // строительства — для него при наличии отряда вместо описания показывается
  // ссылка «Подробнее» (ключевые точки и ландшафт строениями не являются)
  const isBuildingOnCell = Boolean(specialBuilding
    || (token && (checkIfCentralQuarter(token) || Object.values(COMMON_BUILDING_TOKENS).includes(token))));

  // Для гильдии/храма описание не показываем никогда (даже без отряда на
  // ячейке) — оно дублирует смысл уже отображенного выше реального дохода
  // и путей влияния, вместо него всегда «Подробнее»
  const isGuildOrTemple = specialBuilding === SPECIAL_BUILDING_TOKENS.GUILD
    || specialBuilding === SPECIAL_BUILDING_TOKENS.TEMPLE;

  // Куда ведет «Подробнее»: для строений — правила фазы строительства
  // (автоскролл до строения выбранной ячейки делает сама ConstructionPhaseRules
  // по inspectedHexKey), для незахваченных никем ключевых точек — раздел
  // «Ключевые точки» (без автоскролла). null — ссылка не показывается
  let detailsRulesSectionId = null;
  if (isBuildingOnCell) {
    detailsRulesSectionId = RULES_SECTION_IDS.CONSTRUCTION_PHASE;
  } else if (!specialBuilding && KEY_POINT_TOKEN_IDS.includes(token) && !displayOwner) {
    detailsRulesSectionId = RULES_SECTION_IDS.KEY_POINTS;
  }

  // Как InfoButton: открывает модалку правил на соответствующей странице
  const handleDetailsClick = () => {
    REGISTRY_SINGLETON.getSoundManager().playSideMenuClick();
    dispatch(showConstructionPhaseInfoModal(detailsRulesSectionId));
  };

  // Заблокировано противником (смежный/стоящий на ячейке вражеский отряд) или
  // повреждением — та же функция, что и для постоянного слоя блокировок на
  // поле (см. addBlockedCellsMarkers в HexGridRenderer). Имеет смысл только
  // относительно владельца строения — без него (например, обзор рудника без
  // хозяина) блокировать физически некого
  const computeIsBlocked = (tokenType) => Boolean(ownerPlayer)
    && checkIfHexBlockedByEnemyUnit(hexKey, tokenType, ownerPlayer.id, units, fieldHexes, fieldSpecialBuildings, buildingsDamage);

  // Крепость достраивается только в конце раунда, а до тех пор ее ключ лежит
  // в fortressesUnderConstruction. Обычной блокировкой строений
  // (checkIfHexBlockedByEnemyUnit) крепость не бывает занята вовсе, зато у нее
  // есть свои два состояния: идет стройка и стройку держит отряд противника
  const isFortressUnderConstruction = specialBuilding === SPECIAL_BUILDING_TOKENS.FORTRESS
    && Boolean(fortressesUnderConstruction?.includes(hexKey));
  const isFortressConstructionBlocked = isFortressUnderConstruction
    && checkIfFortressConstructionBlocked(hexKey, ownerPlayer?.id, units);

  // Специальное строение показывается вместо жетона базового квартала под ним
  let tokenIcon = null;
  let tokenName = null;
  let incomeStat = null;
  let incomeLabelClassName = null;
  let description = null;
  let isBlocked = false;
  // Пустая ячейка в зоне влияния кварталов: описание короткое (в отличие от
  // BuildingDescription строений) и помещается в футере даже вместе со
  // статами отряда — в отличие от общего правила ниже, «Подробнее» не нужно
  let showDescriptionEvenWithUnit = false;

  if (specialBuilding) {
    const config = SPECIAL_BUILDING_CONFIGS.find((c) => c.id === specialBuilding);
    if (config) {
      tokenIcon = (
        <img
          className={'inspected-cell-panel_token-img'}
          src={config.buttonImage || config.image}
          alt={config.label}
        />
      );
      tokenName = config.label;
    }
    if (!isGuildOrTemple) {
      description = <BuildingDescription buildingId={specialBuilding} detailedMode={true} renderPart={'description'}/>;
    }
    isBlocked = computeIsBlocked(specialBuilding);

    if (ownerPlayer) {
      const enemyCities = getEnemyCitiesForPlayer(ownerPlayer.color, fieldCitiesCache);
      if (specialBuilding === SPECIAL_BUILDING_TOKENS.GUILD) {
        incomeLabelClassName = 'text-coins-dark';
        incomeStat = (
          <CoinsStat
            text={calcGuildIncome(hexKey, ownerPlayer, enemyCities, fieldHexes)}
            noShrinkOnMobile={true}
            isDarkColor={true}
          />
        );
      } else if (specialBuilding === SPECIAL_BUILDING_TOKENS.TEMPLE) {
        incomeLabelClassName = 'text-win-points-dark';
        incomeStat = (
          <WinPointsStat
            text={calcTempleIncome(hexKey, ownerPlayer, enemyCities, fieldHexes)}
            noShrinkOnMobile={true}
            isDarkColor={true}
          />
        );
      }
    }
  } else if (token) {
    if (checkIfCentralQuarter(token)) {
      tokenIcon = <CentralQuarterIcon token={token} className={'inspected-cell-panel_central-quarter-icon'}/>;
      // Перенос явный: на десктопе он задает ширину колонки жетона по длинному
      // слову (см. InspectedCellPanel.css), на телефоне это обычный пробел
      tokenName = 'Центральный\nквартал';
      description = <BuildingDescription buildingId={token} detailedMode={true} renderPart={'description'}/>;
      isBlocked = computeIsBlocked(token);
    } else if (token === TERRAIN_TOKENS.MOUNTAIN || token === TERRAIN_TOKENS.SEA) {
      const config = TERRAIN_CONFIGS.find((c) => c.id === token);
      if (config) {
        // Гора в панели — та же, что на поле этой партии: ее задает подложка.
        // Поле нарисовано подложкой СЦЕНЫ: если в состоянии подложки нет или
        // она неизвестна (старая запись матча), сцена берет случайную (см.
        // GameScene.preload) — тогда и гору смотрим по ней
        const fieldGroundBackground = REGISTRY_SINGLETON.getGameScene()?.groundBackgroundId ?? groundBackground;
        const isMountain = token === TERRAIN_TOKENS.MOUNTAIN;
        const terrainImage = isMountain ? getMountainImage(fieldGroundBackground) : config.image;
        tokenIcon = (
          <img
            className={`inspected-cell-panel_token-img${isMountain ? ' inspected-cell-panel_token-img_mountain' : ''}`}
            src={terrainImage}
            alt={config.label}
          />
        );
        tokenName = config.label;
      }
      description = token === TERRAIN_TOKENS.MOUNTAIN ? MOUNTAIN_DESCRIPTION : SEA_DESCRIPTION;
    } else {
      const config = COMMON_BUILDING_CONFIGS.find((c) => c.id === token)
        || KEY_POINT_CONFIGS.find((c) => c.id === token);

      // Ферма, смежная работающему складу, производит 2 снабжения вместо 1 —
      // здесь подменяем иконку (farm 0_2.png) и передаем реальное число в
      // BuildingDescription через farmSupplyCount. Только для этой панели:
      // без этого пропа (тулбар строительства, оверлей правил через
      // «Подробнее») текст всегда показывает стандартный вариант с 1.
      // Склад ищется на поле вместе с планом строительства — как и для
      // картинки фермы на самом поле (см. calculateTokenImage): иначе рядом
      // с запланированным складом панель спорила бы с тем, что нарисовано
      let farmSupplyCount;
      if (token === COMMON_BUILDING_TOKENS.FARM) {
        const isAdjacentToWarehouse = checkIfAdjacentToWorkingWarehouse(
          hexKey, fieldSpecialBuildings, units, players, fieldCitiesCache,
          currentPlayer, fieldHexes, buildingsDamage
        );
        farmSupplyCount = isAdjacentToWarehouse ? FARM_SUPPLY_WITH_WAREHOUSE_COUNT : FARM_SUPPLY_STANDARD_COUNT;
      }

      // Казарма, смежная работающему военному лагерю, растит отряд за
      // снабжение вдвое — и на поле нарисована с двумя мечами (barracks_2).
      // Здесь то же самое, как у фермы: подменяем иконку и передаем реальный
      // прирост в BuildingDescription через barracksSupplySize. Проверка — та
      // же функция, что выбирает картинку казармы на поле (вместе с планом
      // строительства), поэтому панель с полем не спорит
      let barracksSupplySize;
      if (token === COMMON_BUILDING_TOKENS.BARRACKS
        && checkIfBarracksNearWorkingMilitaryCamp(hexKey, state, currentPlayer)
      ) {
        barracksSupplySize = UNIT_SIZE_FOR_SUPPLY_WITH_MILITARY_CAMP;
      }

      if (config) {
        let tokenImageSrc = config.image;
        if (farmSupplyCount === FARM_SUPPLY_WITH_WAREHOUSE_COUNT) {
          tokenImageSrc = '/img/hexes/farm 0_2.370d7e0c.png';
        } else if (barracksSupplySize !== undefined) {
          tokenImageSrc = BARRACKS_WITH_MILITARY_CAMP_ICON;
        }
        tokenIcon = <img className={'inspected-cell-panel_token-img'} src={tokenImageSrc} alt={config.label}/>;
        tokenName = config.label;
      }
      description = (
        <BuildingDescription
          buildingId={token}
          detailedMode={true}
          renderPart={'description'}
          farmSupplyCount={farmSupplyCount}
          barracksSupplySize={barracksSupplySize}
        />
      );
      isBlocked = computeIsBlocked(token);

      if (KEY_POINT_TOKEN_IDS.includes(token)) {
        if (token === KEY_POINT_TOKENS.RUINS || token === KEY_POINT_TOKENS.SUPER_RUINS) {
          incomeLabelClassName = 'text-win-points-dark';
          incomeStat = (
            <WinPointsStat
              text={calcRuinsIncome(token, ownerPlayer, round, ruinsIncomeRoundLimit)}
              noShrinkOnMobile={true}
              isDarkColor={true}
            />
          );
        } else {
          incomeLabelClassName = 'text-coins-dark';
          incomeStat = (
            <CoinsStat
              text={calcMineIncome(hexKey, token, ownerPlayer, fieldCitiesCache, units, fieldHexes, fieldSpecialBuildings, buildingsDamage)}
              noShrinkOnMobile={true}
              isDarkColor={true}
            />
          );
        }

        // Ключевая точка, не захваченная никем (в т.ч. нейтралом), —
        // подсказка, как ее захватить (BuildingDescription для ключевых точек
        // пуст); флаг центрального квартала — цветом игрока за этим клиентом
        if (!displayOwner) {
          description = (
            <>
              <div className={'policy-description_line'}>
                Для захвата постройте
                <CentralQuarterIcon
                  token={`${CENTRAL_QUARTER_PREFIX}${actingPlayer?.color || NEUTRAL_PLAYER_COLOR}`}
                  className={'inspected-cell-panel_capture-central-quarter-icon'}
                />
                или
                <img
                  className={'policy-description-icon-building policy-description-icon-building-margin-left-only'}
                  src={'/img/hexes/quarter.f69660fa.png'}
                  alt={'Базовый квартал'}
                  title={'Базовый квартал'}
                />
              </div>
              <div className={'policy-description_line policy-description_line-margin-top'}>
                смежно этой ячейке.
              </div>
            </>
          );
        }
      }
    }
  } else {
    // Ни жетона, ни спецстроения — пустая ячейка (например, отряд, стоящий
    // просто на траве вне города); заблокированность/доход тут неприменимы
    tokenIcon = <MaskedIcon className={'inspected-cell-panel_token-img'} src={'/img/icons/cell.png'} alt={'Пустая ячейка'}/>;
    tokenName = 'Пустая ячейка';

    // В зоне влияния кварталов (2 ячейки от любого квартала) — та же
    // штриховка поверх cell.png, что и в постоянном оверлее на поле
    // (см. HexGridRenderer.updateQuarterZoneOverlay): более редкая
    // (base_quarter_allowed.png), если тут еще можно построить базовый
    // квартал, иначе — сплошная (quarter_zone.png), нельзя никакой
    if (calculateQuarterZoneCells(state).has(hexKey)) {
      const isBaseQuarterAllowed = calculateBaseQuarterAllowedCells(state).has(hexKey);

      tokenIcon = (
        <div className={'inspected-cell-panel_empty-cell-icon'}>
          <MaskedIcon src={'/img/icons/cell.png'} alt={'Пустая ячейка'}/>
          <img
            src={isBaseQuarterAllowed ? '/img/icons/base_quarter_allowed.png' : '/img/icons/quarter_zone.png'}
            alt={'Зона влияния кварталов'}
          />
        </div>
      );

      const centralQuarterIcon = <img
        className={'policy-description-icon-building policy-description-icon-building-margin-left'}
        src={'/img/icons/central quarter.7dd51bfd.png'}
        alt={'Центральный квартал'}
        title={'Центральный квартал'}
      />;
      const baseQuarterIcon = <img
        className={'policy-description-icon-building policy-description-icon-building-margin-left'}
        src={'/img/hexes/quarter.f69660fa.png'}
        alt={'Базовый квартал'}
        title={'Базовый квартал'}
      />;

      description = isBaseQuarterAllowed ? (
        <>
          <div className={'policy-description_line policy-description_line-margin-top'}>
            Такая штриховка на ячейке означает,
          </div>
          <div className={'policy-description_line policy-description_line-margin-top'}>
            что здесь <span className={'text-war'}>нельзя построить</span>
          </div>
          <div className={'policy-description_line policy-description_line-margin-top'}>
            Центральный квартал {centralQuarterIcon}.
          </div>
          <div className={'policy-description_line policy-description_line-margin-top'}>
            Но <span className={'text-success'}>можно построить</span>
          </div>
          <div className={'policy-description_line policy-description_line-margin-top'}>
            Базовый квартал {baseQuarterIcon}.
          </div>
        </>
      ) : (
        <>
          <div className={'policy-description_line policy-description_line-margin-top'}>
            Такая штриховка на ячейке означает,
          </div>
          <div className={'policy-description_line policy-description_line-margin-top'}>
            что здесь <span className={'text-war'}>нельзя построить</span>
          </div>
          <div className={'policy-description_line policy-description_line-margin-top'}>
            ни Центральный квартал {centralQuarterIcon},
          </div>
          <div className={'policy-description_line policy-description_line-margin-top'}>
            ни Базовый квартал {baseQuarterIcon}.
          </div>
        </>
      );
      showDescriptionEvenWithUnit = true;
    }
  }

  // Отряд на ячейке — как выбранный отряд на этапе найма фазы войны; кнопка
  // улучшения недоступна (isCurrentPlayerOwner: false), так как сейчас чужой ход
  let unitForStatsDisplay = null;
  if (unit) {
    const unitOwner = getPlayerById(players, unit.ownerId) || getNeutralPlayerMock();
    const unitType = getUnitTypesForRecruitment(unitOwner).find((item) => item.id === unit.type);
    if (unitType) {
      // В фазе снабжения, пока свой ход текущего игрока: то же условие, что и
      // для индикатора на поле (см. HexGridRenderer.renderUnit) — итоговое
      // unit.additionalSupply на этот раунд еще не посчитано (считается при
      // завершении фазы), поэтому источник правды тут — pendingSupply.
      // В остальных случаях (чужой отряд, другая фаза) — обычный
      // персистентный флаг unit.additionalSupply.
      // В одновременной фазе снабжения сетевой игры — те же отряды, чье
      // снабжение видно на поле (getSupplyPreviewPlayers): свои у участника,
      // все у зрителя. Чужую незаконченную раздачу участник не видит ни на поле,
      // ни здесь; в поочередной фазе это, как и было, текущий игрок
      const isOwnSupplyPhaseTurn = phase === PHASES.SUPPLY
        && getSupplyPreviewPlayers(state).some((player) => player.id === unit.ownerId);
      const hasAdditionalSupplyIndicator = isOwnSupplyPhaseTurn
        ? Boolean(checkIfUnitHasPendingAdditionalSupply(unit.id, pendingSupply))
        : Boolean(unit.additionalSupply);

      unitForStatsDisplay = {
        ...unitType,
        id: unit.id,
        ownerId: unit.ownerId,
        // factionId/capturedFromOwnerId - для отображения иконки соблазнённого
        // Суккубом отряда так же, как на игровом поле (см. UnitIcon,
        // getUnitIconDisplayProps): исходный скин и двухцветность вместо
        // скина текущего владельца одним цветом
        factionId: unit.factionId,
        capturedFromOwnerId: unit.capturedFromOwnerId,
        hexKey: unit.hexKey,
        type: unitType.id,
        size: getUnitSizeWithPendingRecruitment(unit, pendingRecruitmentActions),
        maxSize: unit.maxSize,
        additionalSupply: hasAdditionalSupplyIndicator,
        isCurrentPlayerOwner: false,
      };
    }
  }

  // При отряде на ячейке (обе колонки футера разом не помещаются с полным
  // описанием), а также всегда для гильдии/храма — описание заменяется
  // ссылкой «Подробнее». Исключение — showDescriptionEvenWithUnit (пустая
  // ячейка в зоне влияния кварталов): описание короткое, помещается всегда
  // На десктопе описание строения помещается в футере вместе со статами
  // отряда — подменять его ссылкой там незачем (см. useDesktopLayout: та же
  // граница, что и у раскладки в три колонки). Гильдия и храм остаются со
  // ссылкой на любом экране: описания у них нет вовсе (см. isGuildOrTemple)
  const shouldShowDetailsLinkInstead = ((unit && !isDesktopLayout) || isGuildOrTemple)
    && !showDescriptionEvenWithUnit;

  // Отметки состояния: запланировано, стройка крепости, блокировка
  const statusLines = (
    <>
      {isPlannedBuilding && (
        <div
          className={'inspected-cell-panel_blocked-line text-construction'}
          title={'Строение в плане строительства: оно будет построено в ваш ход, если план останется выполнимым.'}
        >
          <div className={'inspected-cell-panel_plan-icon'}/>
          <span>Запланировано</span>
        </div>
      )}

      {isFortressUnderConstruction && !isFortressConstructionBlocked && (
        <div
          className={'inspected-cell-panel_blocked-line text-construction'}
          title={'Крепость достраивается в конце раунда.'}
        >
          <div className={'inspected-cell-panel_construction-icon'}/>
          <span>Строится</span>
        </div>
      )}

      {isFortressConstructionBlocked && (
        <div
          className={'inspected-cell-panel_blocked-line text-war'}
          title={'Стройка не завершится, пока на крепости или смежно с ней стоит отряд противника.'}
        >
          <img
            className={'inspected-cell-panel_construction-blocked-icon'}
            src={'/img/icons/block red outline.png'}
            alt={'Строительство заблокировано'}
          />
          <span>Строительство заблокировано</span>
        </div>
      )}

      {isBlocked && (
        <div
          className={'inspected-cell-panel_blocked-line text-war'}
          title={'Строения и ключевые точки блокируются, если они повреждены или на них либо смежно с ними стоит отряд противника (в том числе нейтральный).\nФермы, казармы и ключевые точки — также когда заблокированы все смежные с ними кварталы владельца.'}
        >
          <div className={'inspected-cell-panel_blocked-icon'}/>
          <span>Заблокировано</span>
        </div>
      )}
    </>
  );

  const ownerBlock = displayOwner && (
    <div className={'inspected-cell-panel_owner'} title={`Владелец: ${displayOwner.name}`}>
      <div className={'inspected-cell-panel_owner-player'}>
        <FactionSkinIcon
          imgClassName={'inspected-cell-panel_owner-faction-icon'}
          factionId={displayOwner.factionId}
          skinId={displayOwner.selectedSkin}
          alt={displayOwner.factionId}
        />
        <span
          className={'inspected-cell-panel_owner-name'}
          style={{color: getPlayerSurfaceColor(displayOwner.color)}}
        >
          {displayOwner.name}
        </span>
      </div>
    </div>
  );

  const incomeLine = incomeStat && (
    <div className={'policy-description_line inspected-cell-panel_income'}>
      <span className={`${incomeLabelClassName} policy-description-icon-margin-right`}>
        Доход
      </span>
      {incomeStat}
    </div>
  );

  // Ссылка ведет на detailsRulesSectionId: строения — фаза строительства,
  // незахваченные ключевые точки — «Ключевые точки»
  const descriptionBlock = shouldShowDetailsLinkInstead ? (
    detailsRulesSectionId && (
      <button
        className={'inspected-cell-panel_details-link text-controls'}
        onClick={handleDetailsClick}
      >
        <MaskedIcon
          className={'inspected-cell-panel_details-link-icon'}
          src={'/img/icons/info.png'}
          alt={'info'}
          tone={MASKED_ICON_TONES.CONTROLS}
        />
        <span>Подробнее</span>
      </button>
    )
  ) : (
    description && (
      <div className={'inspected-cell-panel_description compact-building-description token-hero-lines'}>
        {description}
      </div>
    )
  );

  // Вспышки смены ячейки перезапускает key: и при переходе на другую ячейку, и когда на
  // этой появилось запланированное строение
  const cellKey = `${hexKey}:${specialBuilding || token || ''}`;

  return (
    <div className={'inspected-cell-panel'}>
      <div className={'inspected-cell-panel_cell'} style={getPlayerColorStyle(displayOwner?.color)}>
        <span key={`sweep-${cellKey}`} className={'inspected-cell-panel_sweep'} aria-hidden={true}/>

        {tokenName && <TokenHero animationKey={cellKey} isFramed={Boolean(displayOwner)}>{tokenIcon}</TokenHero>}

        <div key={cellKey} className={'inspected-cell-panel_main'}>
          <div className={'inspected-cell-panel_title-row'}>
            {tokenName && <div className={'inspected-cell-panel_name token-hero-name'}>{tokenName}</div>}
            {ownerBlock}
          </div>
          {statusLines}
          {incomeLine}
          {descriptionBlock}
        </div>
      </div>

      {/* Не в условии: когда на ячейке нет отряда, карточка складывается, а не исчезает */}
      <RecruitUnitCard unit={unitForStatsDisplay} isWing={isDesktopLayout} isStacked={!isDesktopLayout} isInspect={true}/>
    </div>
  );
};

export default InspectedCellPanel;
